import { useCallback, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { isEmpty } from 'lodash-es';
import { toasty } from '../../../../common/forms/FormElements';
import { useJobQueryKey } from '../../common/useJobQueryKey.hook';
import { JobSavingContext } from './JobContext';

const defaultSetSaving = () => { };
const defaultMutationHandler = (resource) => resource;

export const useUpdateAssignment = ({
    jobAssignmentId,
    jobId,
    onMutate = defaultMutationHandler,
    onMutateGroup = defaultMutationHandler,
    mutateLinkedEquipment = false,
    mutateOnSuccess = false,
}) => {
    const setSaving = useContext(JobSavingContext) || defaultSetSaving;
    const { queryKey, date } = useJobQueryKey();

    const queryClient = useQueryClient();

    const handleSuccess = useCallback(() => {
        setSaving(false);
    }, [setSaving]);

    const handleMutate = useCallback((mutationVariables) => {
        setSaving(true);

        const prev = queryClient.getQueryData(queryKey);

        queryClient.setQueryData(queryKey, (oldData) => {
            if (oldData?.rows) {
                const day = moment(date).day();

                const data = {
                    ...oldData,
                    rows: oldData.rows.map((job) => {
                        if (job.id !== jobId && !job.jobIdsInGroup?.includes(jobId)) {
                            return job;
                        }

                        let linkedEquipmentIds = [];

                        const mappedJob = {
                            ...job,
                            assignments: {
                                ...job.assignments,
                                [day]: {
                                    ...job.assignments[day],
                                    ...onMutateGroup(job.assignments[day], mutationVariables),
                                    staff: job.assignments[day].staff.reduce((results, employee) => {
                                        if (employee.jobAssignmentId === jobAssignmentId) {
                                            if (mutateLinkedEquipment && !isEmpty(employee?.assignment?.assignedEquipment)) {
                                                linkedEquipmentIds = [
                                                    ...linkedEquipmentIds,
                                                    ...employee.assignment.assignedEquipment.map((eq) => eq.id),
                                                ];
                                            }

                                            return [
                                                ...results,
                                                onMutate(employee, true, mutationVariables, results),
                                            ];
                                        }

                                        return [
                                            ...results,
                                            onMutate(employee, false, mutationVariables, results),
                                        ];
                                    }, []),
                                    equipment: job.assignments[day].equipment.reduce((results, equipment) => {
                                        if (equipment.jobAssignmentId === jobAssignmentId) {
                                            return [
                                                ...results,
                                                onMutate(equipment, true, mutationVariables, results),
                                            ];
                                        }

                                        return [
                                            ...results,
                                            onMutate(equipment, false, mutationVariables, results),
                                        ];
                                    }, []),
                                },
                            },
                        };

                        if (!mutateLinkedEquipment) {
                            return mappedJob;
                        }

                        return {
                            ...mappedJob,
                            assignments: {
                                ...mappedJob.assignments,
                                [day]: {
                                    ...mappedJob.assignments[day],
                                    equipment: mappedJob.assignments[day].equipment.reduce((results, equipment) => {
                                        if (equipment.assignment && linkedEquipmentIds.includes(equipment.assignment.id)) {
                                            return [
                                                ...results,
                                                onMutate(equipment, true, mutationVariables, results),
                                            ];
                                        }

                                        return [
                                            ...results,
                                            onMutate(equipment, false, mutationVariables, results),
                                        ];
                                    }, []),
                                },
                            },
                        };
                    }),
                };

                return data;
            }

            return oldData;
        });

        return { previousData: prev };
    }, [date, queryKey, queryClient, onMutate, jobAssignmentId, setSaving, jobId, onMutateGroup, mutateLinkedEquipment]);

    const handleError = useCallback((error, _, context) => {
        if (context?.previousData) {
            queryClient.setQueryData(queryKey, context.previousData);
        }

        console.error(error);
        toasty.error('An error occurred while updating the job assignment');

        setSaving(false);
    }, [queryClient, queryKey, setSaving]);


    if (mutateOnSuccess) {
        return {
            onSettled: handleSuccess,
            onError: handleError,
            onSuccess: () => {
                handleMutate();
                handleSuccess();
            },
        };
    }

    return {
        onMutate: handleMutate,
        onError: handleError,
        onSuccess: handleSuccess,
        onSettled: handleSuccess,
    };
};